/**
 * A finite projective plane over a prime field produces a complete matching deck:
 * q² + q + 1 cards, the same number of symbols, and q + 1 symbols on each card.
 * Any two cards share exactly one symbol; each symbol appears q + 1 times.
 */
export type DeckOrder = 3 | 5 | 7;

export interface GameCard {
  id: number;
  symbols: number[];
}

export interface DeckStats {
  cards: number;
  symbols: number;
  symbolsPerCard: number;
}

function assertOrder(order: DeckOrder): void {
  if (![3, 5, 7].includes(order)) {
    throw new RangeError("L’ordre du jeu doit être 3, 5 ou 7.");
  }
}

export function getDeckStats(order: DeckOrder = 7): DeckStats {
  assertOrder(order);
  const count = order * order + order + 1;
  return { cards: count, symbols: count, symbolsPerCard: order + 1 };
}

/** Symbol IDs are zero-based, so a deck of order q uses the first q² + q + 1 assets. */
export function generateDeck(order: DeckOrder = 7): GameCard[] {
  assertOrder(order);
  const cards: GameCard[] = [];
  const infinityStart = order * order;

  // Affine lines y = slope * x + intercept, plus their point at infinity.
  for (let slope = 0; slope < order; slope += 1) {
    for (let intercept = 0; intercept < order; intercept += 1) {
      const symbols: number[] = [];
      for (let x = 0; x < order; x += 1) {
        symbols.push(x * order + ((slope * x + intercept) % order));
      }
      symbols.push(infinityStart + slope);
      cards.push({ id: cards.length, symbols });
    }
  }

  // Vertical affine lines x = constant share their own point at infinity.
  for (let x = 0; x < order; x += 1) {
    const symbols = Array.from({ length: order }, (_, y) => x * order + y);
    symbols.push(infinityStart + order);
    cards.push({ id: cards.length, symbols });
  }

  // The line at infinity connects all directions.
  cards.push({
    id: cards.length,
    symbols: Array.from(
      { length: order + 1 },
      (_, index) => infinityStart + index,
    ),
  });

  return cards;
}

/** Validate a complete deck, including its pairwise intersection invariant. */
export function validateDeck(cards: readonly GameCard[]): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  if (cards.length === 0) {
    return { valid: false, errors: ["Le jeu ne contient aucune carte."] };
  }

  const symbolsPerCard = cards[0].symbols.length;
  if (symbolsPerCard < 2) {
    errors.push("Chaque carte doit contenir au moins deux symboles.");
  }

  const expectedCount = symbolsPerCard * symbolsPerCard - symbolsPerCard + 1;
  if (cards.length !== expectedCount) {
    errors.push(
      `Le jeu doit contenir ${expectedCount} cartes, contre ${cards.length}.`,
    );
  }

  const cardIds = new Set<number>();
  const occurrences = new Map<number, number>();
  const symbolSets = cards.map((card) => {
    if (!Number.isInteger(card.id) || card.id < 0) {
      errors.push(`L’identifiant de carte ${card.id} est invalide.`);
    }
    if (cardIds.has(card.id)) {
      errors.push(
        `L’identifiant de carte ${card.id} est présent plusieurs fois.`,
      );
    }
    cardIds.add(card.id);

    if (card.symbols.length !== symbolsPerCard) {
      errors.push(`La carte ${card.id} n’a pas ${symbolsPerCard} symboles.`);
    }
    const symbols = new Set(card.symbols);
    if (symbols.size !== card.symbols.length) {
      errors.push(`La carte ${card.id} contient des symboles en double.`);
    }
    for (const symbol of symbols) {
      if (!Number.isInteger(symbol) || symbol < 0) {
        errors.push(
          `Le symbole ${symbol} de la carte ${card.id} est invalide.`,
        );
      }
      occurrences.set(symbol, (occurrences.get(symbol) ?? 0) + 1);
    }
    return symbols;
  });

  if (occurrences.size !== expectedCount) {
    errors.push(
      `Le jeu doit utiliser ${expectedCount} symboles différents, contre ${occurrences.size}.`,
    );
  }
  for (const [symbol, count] of occurrences) {
    if (count !== symbolsPerCard) {
      errors.push(
        `Le symbole ${symbol} apparaît ${count} fois au lieu de ${symbolsPerCard}.`,
      );
    }
  }

  for (let left = 0; left < cards.length; left += 1) {
    for (let right = left + 1; right < cards.length; right += 1) {
      let matches = 0;
      for (const symbol of symbolSets[left]) {
        if (symbolSets[right].has(symbol)) matches += 1;
      }
      if (matches !== 1) {
        errors.push(
          `Les cartes ${cards[left].id} et ${cards[right].id} partagent ${matches} symboles au lieu d’un.`,
        );
      }
    }
  }

  return { valid: errors.length === 0, errors };
}

/** Small deterministic PRNG for reproducible decks in browser and server. */
function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let value = Math.imul(state ^ (state >>> 15), 1 | state);
    value = (value + Math.imul(value ^ (value >>> 7), 61 | value)) ^ value;
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffle<T>(values: T[], random: () => number): T[] {
  for (let index = values.length - 1; index > 0; index -= 1) {
    const other = Math.floor(random() * (index + 1));
    [values[index], values[other]] = [values[other], values[index]];
  }
  return values;
}

/** Shuffle cards and their symbols without changing stable card or symbol IDs. */
export function shuffledDeck(
  order: DeckOrder = 7,
  seed: number = 0,
): GameCard[] {
  if (!Number.isFinite(seed))
    throw new RangeError("La graine du jeu doit être un nombre fini.");
  const random = seededRandom(seed);
  const cards = generateDeck(order);
  for (const card of cards) shuffle(card.symbols, random);
  return shuffle(cards, random);
}
